import type { Locale } from "@src/i18n/locale-catalog";
import type { ShareDialog } from "@src/features/sharing/ShareDialogs";
import { navigateToLocale } from "../locale-navigation";
import { useRef, useState } from "react";
import type { TransformationViewportHandle } from "@src/features/visualization/TransformationViewport";
import type { RefObject } from "react";
import { useAppStoreApi } from "../state/app-store-context";
import { useShareLink } from "./use-share-link";
import { clearLocationHash, useShareRoute } from "./use-share-route";

export function useShareController({
  locale,
  scene,
  setDialog,
  onLocaleChange,
}: {
  locale: Locale;
  scene: RefObject<TransformationViewportHandle | null>;
  setDialog: (dialog: ShareDialog | null) => void;
  onLocaleChange?: (locale: Locale) => void;
}) {
  const store = useAppStoreApi();
  const [ready, setReady] = useState(false);
  const navigationAttemptRef = useRef(0);
  const routedHashRef = useRef<string | null>(null);

  useShareRoute({
    store,
    scene,
    setDialog,
    navigationAttemptRef,
    routedHashRef,
    onLocaleChange,
    setReady,
  });
  const sharing = useShareLink({
    store,
    scene,
    setDialog,
    locale,
    navigationAttemptRef,
  });

  const changeLocale = (next: Locale) => {
    if (!navigateToLocale({ next, current: locale })) return;
    ++navigationAttemptRef.current;
    store.getState().cancelShareLoad();
    routedHashRef.current = "";
    setDialog(null);
    onLocaleChange?.(next);
  };
  const clearShareRoute = () => {
    ++navigationAttemptRef.current;
    clearLocationHash();
    routedHashRef.current = "";
    setDialog(null);
  };
  const recoverInvalidShare = clearShareRoute;
  const resetApp = () => {
    clearShareRoute();
    store.getState().resetApp();
    scene.current?.resetView(2);
    scene.current?.resetView(3);
  };

  return {
    ready,
    ...sharing,
    changeLocale,
    recoverInvalidShare,
    resetApp,
  };
}
